import * as React from "react"
import { Button, Link, Spacer, ThemeCss } from "gatsby-interface"
import { MdSubdirectoryArrowLeft } from "react-icons/md"
import {
  HttpsOnBadge,
  GeneratingCertBadge,
  DomainErrorMessage,
  WarningIcon,
} from "./DomainDetails.elements"
import {
  HostingDomain,
  CertificateStatus,
  HostingCertificate,
} from "@modules/graphql/types"
import { gatsbyCloudCdnIntegrationConfig as text } from "@modules/locales/default.json"
import { useJustCreatedCertificate } from "../hooks/useJustCreatedCertificate"
import { Bull } from "@modules/ui/components/Bull"

// the value in seconds wich differentiates the old certs from the ones just created.
// for the last ones we render a special message that the cert is being uploaded to cdn
export const CERT_CREATION_THRESHOLD_IN_SECONDS = 90

const rowCss: ThemeCss = theme => ({
  minHeight: theme.space[8],
  marginBottom: theme.space[3],
  alignItems: `center`,
  display: `flex`,

  "&:last-child": {
    marginBottom: 0,
  },
})

const secondaryDomainIconCss: ThemeCss = theme => ({
  transform: `rotate(90deg)`,
  color: theme.colors.purple[60],
  marginRight: theme.space[2],
})

const domainCss: ThemeCss = theme => ({
  fontWeight: theme.fontWeights.semiBold,
  lineHeight: theme.lineHeights.solid,
  color: theme.colors.grey[50],

  "&:hover": {
    color: theme.colors.purple[60],
  },
})

const verifiedDomainCss: ThemeCss = theme => ({
  color: theme.colors.grey[80],
})

const domainWithErrorCss: ThemeCss = theme => ({
  color: theme.colors.red[80],

  "&:hover": {
    color: `inherit`,
  },
})

const actionsCss: ThemeCss = _theme => ({
  marginLeft: `auto`,
})

const validationCss: ThemeCss = theme => ({
  alignItems: `center`,
  display: `flex`,
  marginLeft: theme.space[4],
  fontSize: theme.fontSizes[0],
  color: theme.colors.grey[60],
})

const domainLevelCss: ThemeCss = theme => ({
  fontSize: theme.fontSizes[0],
  color: theme.colors.grey[60],
})

export type DomainDetailsProps = {
  domain: HostingDomain
  siteId: string
  onRemove: () => void
  onRequestCertificate: () => void
  certificate?: HostingCertificate
  threshold?: number
  hasSecondary?: boolean
  validating?: boolean
  onMakePrimary?: () => void
}

export function DomainDetails({
  domain,
  onRemove,
  onMakePrimary,
  onRequestCertificate,
  certificate,
  threshold = CERT_CREATION_THRESHOLD_IN_SECONDS,
  hasSecondary,
  validating,
}: DomainDetailsProps) {
  const {
    id,
    verified,
    errorMessage,
    cloudCdnDefault,
    certificateStatus,
    primaryDomainId,
  } = domain
  const { isJustCreatedCert } = useJustCreatedCertificate({
    certificate,
    certificateStatus,
    threshold,
  })

  return (
    <React.Fragment>
      <div
        css={theme => [
          rowCss(theme),
          primaryDomainId && {
            marginTop: `-${theme.space[4]}`,
          },
        ]}
      >
        {primaryDomainId && (
          <MdSubdirectoryArrowLeft css={secondaryDomainIconCss} />
        )}

        {!errorMessage ? (
          <Link
            variant="SIMPLE"
            href={`https://${id}`}
            css={theme => [
              domainCss(theme),
              verified && verifiedDomainCss(theme),
            ]}
          >
            {id}
          </Link>
        ) : (
          <span css={theme => [domainCss(theme), domainWithErrorCss(theme)]}>
            {id}
          </span>
        )}

        {cloudCdnDefault && <HttpsOnBadge />}

        {certificateStatus === CertificateStatus.Issued &&
          !isJustCreatedCert && <HttpsOnBadge />}

        {certificateStatus === CertificateStatus.Issued &&
          isJustCreatedCert && (
            <GeneratingCertBadge message={text.messages.uploadingCertificate} />
          )}

        {!errorMessage && certificateStatus === CertificateStatus.Requested && (
          <GeneratingCertBadge message={text.messages.generatingCertificate} />
        )}

        {!verified && !cloudCdnDefault && !errorMessage && (
          <span css={validationCss}>
            <WarningIcon />
            {validating ? text.messages.validating : text.messages.notValidated}
          </span>
        )}

        {(primaryDomainId || hasSecondary) && (
          <span css={domainLevelCss}>
            <Bull />
            {primaryDomainId ? `Redirects to primary` : `Primary domain`}
          </span>
        )}

        <div css={actionsCss}>
          {primaryDomainId && (
            <Button variant="GHOST" size="S" onClick={onMakePrimary}>
              {text.actions.makePrimary}
            </Button>
          )}

          {certificateStatus === CertificateStatus.Deleted && (
            <Button variant="GHOST" size="S" onClick={onRequestCertificate}>
              {text.actions.requestCertificate}
            </Button>
          )}

          {!cloudCdnDefault && (
            <Button variant="GHOST" size="S" onClick={onRemove}>
              {text.actions.remove}
            </Button>
          )}
        </div>
      </div>

      {domain && errorMessage && (
        <React.Fragment>
          <Spacer size={4} />
          <DomainErrorMessage message={errorMessage} />
        </React.Fragment>
      )}
    </React.Fragment>
  )
}
